import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import type { MerchantFacilityDto } from "Infrastructure/Api/Api";
import { isPhoneEmpty } from "Utils/PhoneUtils";

/** Data URL for a base64 image as the API returns it (base64 + extension), or null when absent. */
export const toImageDataUrl = (
  base64: string | null | undefined,
  extension: string | null | undefined,
): string | null => {
  if (!base64) {
    return null;
  }

  const format = extension?.replace(".", "") ?? "jpeg";
  return `data:image/${format};base64,${base64}`;
};

export const hasFacilityPhotos = (facility: MerchantFacilityDto): boolean =>
  facility.hasPhotoOutside && facility.hasPhotoInside;

export const isFacilityComplete = (facility: MerchantFacilityDto): boolean =>
  !!facility.providedServices &&
  !!facility.responsiblePerson &&
  !isPhoneEmpty(facility.phone) &&
  !!facility.email &&
  !!facility.address?.street &&
  !!facility.address?.streetNumber &&
  !!facility.address?.postalCode &&
  !!facility.address?.municipality &&
  facility.yearlyTurnover != null &&
  facility.realCardTurnover != null &&
  facility.averageTransactionValue != null &&
  facility.hasPhotoOutside &&
  facility.hasPhotoInside;

export const facilityDtoToFormModel = (
  facility: Partial<MerchantFacilityDto>,
): Partial<FacilityFormModel> => ({
  ...facility,
  name: facility.name ?? undefined,
  address: {
    street: facility.address?.street ?? "",
    streetNumber: facility.address?.streetNumber ?? "",
    orientationNumber: facility.address?.orientationNumber ?? undefined,
    postalCode: facility.address?.postalCode ?? "",
    municipality: facility.address?.municipality ?? "",
  },
  providedServices: facility.providedServices ?? undefined,
  responsiblePerson: facility.responsiblePerson ?? undefined,
  phone: facility.phone,
  email: facility.email ?? undefined,
  yearlyTurnover: facility.yearlyTurnover ?? undefined,
  realCardTurnover: facility.realCardTurnover ?? undefined,
  estimatedTurnover: facility.estimatedTurnover ?? undefined,
  averageTransactionValue: facility.averageTransactionValue ?? undefined,
  terminalCount: facility.terminalCount ?? undefined,
  softPosCount: facility.softPosCount ?? undefined,
  hours:
    facility.hours?.map((h) => ({
      weekday: h.weekday,
      openTime: h.openTime ?? null,
      closeTime: h.closeTime ?? null,
      isOpenAllDay: h.isOpenAllDay,
      isClosedAllDay: h.isClosedAllDay,
    })) ?? undefined,
  specialHours:
    facility.specialHours?.map((sh) => ({
      date: sh.date,
      openTime: sh.openTime ?? null,
      closeTime: sh.closeTime ?? null,
      isOpenAllDay: sh.isOpenAllDay,
      isClosedAllDay: sh.isClosedAllDay,
    })) ?? undefined,
});
